/**
 * A wrapper class for localStorage that provides namespaced key-value storage
 * with automatic JSON serialization/deserialization.
 *
 * All keys are automatically prefixed with the namespace to avoid collisions
 * with other parts of the application. The class is SSR-safe and gracefully
 * handles environments where localStorage is unavailable.
 *
 * @example
 * // Default global namespace
 * const store = new Store()
 * store.set('user', { name: 'John' })
 * store.get('user') // { name: 'John' }
 *
 * @example
 * // Custom namespace for feature isolation
 * const settingsStore = new Store('settings')
 * settingsStore.set('theme', 'dark') // Stored as 'settings/theme'
 */
class Store {
  /**
   * Creates a new Store instance.
   *
   * @param {string} [prefix='global'] - Namespace prefix for all keys
   * @param {Storage|null} [store] - Storage backend (defaults to localStorage if available)
   */
  constructor(
    prefix = 'global',
    store = typeof window !== 'undefined' ? window.localStorage : null,
  ) {
    /** @type {Storage|null} */
    this.store = store
    /** @type {string} */
    this.prefix = prefix
  }

  /**
   * Generates the full storage key by prepending the namespace prefix.
   *
   * @private
   * @param {string} key - The unprefixed key
   * @returns {string} The full key with prefix (e.g., 'global/myKey')
   */
  _getFullKey(key) {
    return `${this.prefix}/${key}`
  }

  /**
   * Stores a value in localStorage with automatic JSON serialization.
   *
   * @param {string} key - The key to store the value under
   * @param {*} value - The value to store (will be JSON serialized)
   * @returns {void}
   *
   * @example
   * store.set('preferences', { notifications: true, theme: 'dark' })
   */
  set(key, value) {
    if (!this.store) return
    this.store.setItem(this._getFullKey(key), JSON.stringify(value))
  }

  /**
   * Retrieves a value from localStorage with automatic JSON deserialization.
   *
   * @template T
   * @param {string} key - The key to retrieve
   * @param {T} [defaultValue=null] - Value to return if key doesn't exist or on parse error
   * @returns {T|*} The stored value or defaultValue
   *
   * @example
   * const prefs = store.get('preferences', { notifications: false })
   */
  get(key, defaultValue = null) {
    if (!this.store) return defaultValue
    try {
      const item = this.store.getItem(this._getFullKey(key))
      return item !== null ? JSON.parse(item) : defaultValue
    } catch {
      return defaultValue
    }
  }

  /**
   * Checks if a key exists in the store.
   *
   * @param {string} key - The key to check
   * @returns {boolean} True if the key exists, false otherwise
   *
   * @example
   * if (store.has('user')) {
   *   // User data exists
   * }
   */
  has(key) {
    if (!this.store) return false
    return this.store.getItem(this._getFullKey(key)) !== null
  }

  /**
   * Removes a single key from the store.
   *
   * @param {string} key - The key to remove
   * @returns {void}
   *
   * @example
   * store.remove('temporaryData')
   */
  remove(key) {
    if (!this.store) return
    this.store.removeItem(this._getFullKey(key))
  }

  /**
   * Removes all keys that belong to this store's namespace.
   * Does not affect keys from other namespaces or unrelated localStorage entries.
   *
   * @returns {void}
   *
   * @example
   * const userStore = new Store('user')
   * userStore.clear() // Only removes keys starting with 'user/'
   */
  clear() {
    if (!this.store) return
    const keysToRemove = []
    for (let i = 0; i < this.store.length; i++) {
      const key = this.store.key(i)
      if (key?.startsWith(`${this.prefix}/`)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(key => this.store.removeItem(key))
  }

  /**
   * Returns all keys in this store's namespace (without the prefix).
   *
   * @returns {string[]} Array of keys belonging to this namespace
   *
   * @example
   * store.set('a', 1)
   * store.set('b', 2)
   * store.keys() // ['a', 'b']
   */
  keys() {
    if (!this.store) return []
    const result = []
    const prefixWithSlash = `${this.prefix}/`
    for (let i = 0; i < this.store.length; i++) {
      const key = this.store.key(i)
      if (key?.startsWith(prefixWithSlash)) {
        result.push(key.slice(prefixWithSlash.length))
      }
    }
    return result
  }
}

export default Store
